import { useState, useEffect, useCallback } from "react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import AppLayout from "../../components/layout/AppLayout"
import { api } from "../../lib/api"
import { useNotifications } from "../../contexts/NotificationsContext"
import "../../styles/notifications.scss"

type Announcement = {
  id: number
  title: string
  body: string
  published_at: string
  read: boolean
}

type AnnouncementsResponse = {
  announcements: Announcement[]
  total_pages: number
  current_page: number
}

const formatDate = (iso: string) => {
  const d = new Date(iso)
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}`
}

const NotificationsPage = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([])
  const [selected, setSelected] = useState<Announcement | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(true)
  const { refreshUnreadCount } = useNotifications()

  const fetchAnnouncements = useCallback(async (page: number) => {
    setLoading(true)
    try {
      const data = await api.get<AnnouncementsResponse>(`/notifications/announcements?page=${page}`)
      setAnnouncements(data.announcements)
      setTotalPages(data.total_pages)
      setCurrentPage(data.current_page)
      if (data.announcements.length > 0) {
        setSelected(prev => prev ? (data.announcements.find(a => a.id === prev.id) ?? data.announcements[0]) : data.announcements[0])
      } else {
        setSelected(null)
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchAnnouncements(1)
  }, [fetchAnnouncements])

  const handleSelect = async (announcement: Announcement) => {
    setSelected(announcement)
    if (!announcement.read) {
      try {
        await api.post(`/notifications/announcements/${announcement.id}/reads`, {})
        setAnnouncements(prev =>
          prev.map(a => a.id === announcement.id ? { ...a, read: true } : a)
        )
        refreshUnreadCount()
      } catch {
        // ignore
      }
    }
  }

  const handlePageChange = (page: number) => {
    fetchAnnouncements(page)
  }

  return (
    <AppLayout pageTitle="お知らせ">
      <div className="notifications">
        {/* 左：一覧パネル */}
        <div className="notifications-list">
          <div className="notifications-list__header">
            <span>お知らせ一覧</span>
          </div>

          <div className="notifications-list__items">
            {loading ? (
              <div className="notifications-list__empty">読み込み中…</div>
            ) : announcements.length === 0 ? (
              <div className="notifications-list__empty">お知らせはありません</div>
            ) : (
              announcements.map(a => (
                <button
                  key={a.id}
                  className={[
                    "notifications-list__item",
                    selected?.id === a.id ? "notifications-list__item--active" : "",
                    !a.read ? "notifications-list__item--unread" : "",
                  ].filter(Boolean).join(" ")}
                  onClick={() => handleSelect(a)}
                >
                  <div className="notifications-list__item-top">
                    {!a.read && <span className="notifications-list__item-dot" />}
                    <span className="notifications-list__item-title">{a.title}</span>
                  </div>
                  <span className="notifications-list__item-date">{formatDate(a.published_at)}</span>
                </button>
              ))
            )}
          </div>

          {totalPages > 1 && (
            <div className="notifications-pagination">
              <button
                className="notifications-pagination__btn"
                disabled={currentPage <= 1}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                <i className="bi bi-chevron-left" />
              </button>
              <span className="notifications-pagination__info">
                {currentPage} / {totalPages}
              </span>
              <button
                className="notifications-pagination__btn"
                disabled={currentPage >= totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                <i className="bi bi-chevron-right" />
              </button>
            </div>
          )}
        </div>

        {/* 右：詳細パネル */}
        <div className="notifications-detail">
          {selected ? (
            <>
              <div className="notifications-detail__header">
                <h2 className="notifications-detail__title">{selected.title}</h2>
                <span className="notifications-detail__date">{formatDate(selected.published_at)}</span>
              </div>
              <div className="notifications-detail__body">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {selected.body}
                </ReactMarkdown>
              </div>
            </>
          ) : (
            <div className="notifications-detail__empty">
              <i className="bi bi-bell" />
              <p>左のリストからお知らせを選択してください</p>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  )
}

export default NotificationsPage
